import { LEAVE_TYPE_LABEL } from "../../lib/leaveOptions";
import { NO_VALUE, plural } from "../../lib/vocabulary";
import { joinParts } from "../../lib/vocabulary";

// Two types (P5/P8) — SL is gone from the model, so its tint went with it.
const TYPE_TINT = {
  CL: { bg: "bg-blue-50", text: "text-blue-700", dot: "bg-blue-500" },
  PL: { bg: "bg-violet-50", text: "text-violet-700", dot: "bg-violet-500" },
};

const known = (v) => v !== null && v !== undefined;

/**
 * One type's balance tile: accrued · availed · balance, never a bare
 * entitlement number. `extra` carries PL's lapse counter / cap warning line.
 *
 * MISSING IS MISSING (Pattern A / RC-11). This used to read
 * `balance ?? Math.max(0, (accrued || 0) - (availed || 0))`, which INVENTED a
 * balance out of two absent numbers and printed it as a fact: a type the
 * response did not carry rendered "0 / 0", which says an entitlement exists
 * and is exhausted — the difference between "you have no casual leave left"
 * and "we could not read your casual leave". LeaveBalancesPanel already got
 * this right for LOP ("showing it as 0 / 0 would imply one exists"); this is
 * that treatment, promoted. Nothing here computes a balance — the server owns
 * that arithmetic, and a second implementation of it on the client is the
 * drift shape this build exists to delete.
 */
export default function BalanceCard({ type, accrued, availed, balance, testId, extra = null }) {
  const tint = TYPE_TINT[type] || TYPE_TINT.CL;
  const nothingReported = !known(balance) && !known(accrued) && !known(availed);
  // Only a real accrued figure can scale a bar; an absent one leaves it empty
  // rather than full or arbitrarily zero-width-with-meaning.
  const pct =
    known(accrued) && accrued > 0 && known(availed)
      ? Math.min(100, Math.round((availed / accrued) * 100))
      : 0;

  return (
    <div
      className="bg-white border border-callisto rounded-xl p-4"
      data-testid={testId}
    >
      <div className="flex items-center justify-between">
        <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-callisto-muted">
          {joinParts([LEAVE_TYPE_LABEL[type], type])}
        </p>
        <span
          className={`inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full ${tint.bg} ${tint.text}`}
        >
          <span className={`h-1.5 w-1.5 rounded-full ${tint.dot}`} />
          {type}
        </span>
      </div>

      {nothingReported ? (
        <p
          className="mt-2 text-sm text-callisto-muted"
          data-testid={`${testId}-unavailable`}
        >
          Balance not available
        </p>
      ) : (
        <>
          <p className="mt-2 text-2xl font-bold text-callisto-navy">
            {known(balance) ? balance : NO_VALUE}
            <span className="text-base font-medium text-callisto-muted">
              {" "}
              / {known(accrued) ? accrued : NO_VALUE}
            </span>
          </p>
          <p className="mt-0.5 text-[11px] text-callisto-muted">
            {known(availed) ? `${plural(availed, "day")} availed` : "Availed not reported"}
          </p>
        </>
      )}
      {extra}
      <div className="mt-3 h-1.5 w-full bg-callisto-app rounded-full overflow-hidden">
        <div
          className="h-full bg-callisto-gold rounded-full transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}
